import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { resetPassword, verifyResetPasswordVerificationCode } from "../redux/actions/auth";

const schema = yup.object({
  password: yup
    .string()
    .required("New password is required.")
    .min(6, "Password must be atleast 6 characters.")
    .max(15, "Password should not be greater than 15 characters.")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/, "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character"),
  confirmPassword: yup
    .string()
    .required("Confirm password is required.")
    .min(6, "Password must be atleast 6 characters.")
    .max(15, "Password should not be greater than 15 characters.")
    .matches(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/, "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character")
    .oneOf([yup.ref("password"), null], "Password and Confirm Password must match"),
});

const ResetPasswordForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [searchParams] = useSearchParams();
  const [isVerified, setIsVerified] = useState(true);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const disabledCss = " disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed";

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const payload = {
      user_id: searchParams.get("user_id"),
      verification_code: searchParams.get("verification_code"),
    };

    dispatch(verifyResetPasswordVerificationCode(payload, (res) => {
      if (res.status) {
        setIsReady(false);
        setIsVerified(true);
      } else {
        setIsReady(false);
        setIsVerified(false);
        setTimeout(() => {
          navigate("/login");
        }, 10000);
      }
    }))

  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      user_id: searchParams.get("user_id"),
      verification_code: searchParams.get("verification_code"),
      password: data.password,
    };

    dispatch(resetPassword(payload, (res) => {
      setLoading(false);
      if (res.status) {
        setError({
          type: "success",
          message: "Your new password successfully updated. Please login.",
        });
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      } else {
        setError({
          type: "error",
          message: "Unable to reset your password.",
        });
      }
    }));
  };


  return (
    <main className="bg-white">
      {isReady && (
        <div className="w-screen h-screen flex justify-center items-center">
          <span className="ml-2">
            <svg className="animate-spin w-20 h-20 fill-current shrink-0" viewBox="0 0 16 16">
              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
            </svg>
          </span>
        </div>
      )}
      {!isReady && (
        <div className="relative md:flex">
          {/* Content */}
          <div className="md:w-1/2">
            <div className="min-h-screen h-full flex flex-col after:flex-1">
              <div className="flex flex-col justify-center max-w-sm mx-auto px-4 py-8 h-full">
                {/* Header */}
                <div className="flex mb-5">
                  <div className="flex items-center">
                    {/* Logo */}
                    <div className="block w-[40px]">
                      <img alt="Logo" src={require("../assets/img/ShiproneLogo.png")} />
                    </div>
                  </div>
                </div>
                <h1 className="text-3xl text-slate-800 font-bold mb-6">Reset your password</h1>

                {/* Message Feedback */}
                <div className="my-3">
                  {!error ? (
                    ""
                  ) : (
                    <div className={` ${error.type === "error" ? "bg-amber-100 text-amber-600" : "bg-green-600 text-white"} px-3 py-2 rounded`}>
                      <svg className="inline w-3 h-3 shrink-0 fill-current mr-2" viewBox="0 0 12 12">
                        <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                      </svg>
                      <span className="text-sm">{error.message}</span>
                    </div>
                  )}
                </div>

                {/* Form */}

                {isVerified ? (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-4">
                      <Controller
                        name="password"
                        defaultValue=""
                        control={control}
                        render={({ field, formState }) => (
                          <div>
                            <label className="block text-sm font-medium mb-1" htmlFor="password">
                              New Password <span className="text-rose-500">*</span>
                            </label>
                            <div className="relative">
                              <input {...field} className="form-input w-full" type={showPassword ? "text" : "password"} />
                              <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                                {showPassword && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    class="w-4 h-4 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowPassword(!showPassword);
                                    }}
                                  >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                )}
                                {!showPassword && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    class="w-4 h-4 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowPassword(!showPassword);
                                    }}
                                  >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            {formState.errors?.password && <div className="text-xs text-rose-600 pt-1 pl-1">{formState.errors?.password?.message}</div>}
                          </div>
                        )}
                      />
                      <Controller
                        name="confirmPassword"
                        defaultValue=""
                        control={control}
                        render={({ field, formState }) => (
                          <div>
                            <label className="block text-sm font-medium mb-1" htmlFor="confirmPassword">
                              Confirm Password <span className="text-rose-500">*</span>
                            </label>
                            <div className="relative">
                              <input {...field} className="form-input w-full" type={showConfirmPassword ? "text" : "password"} />
                              <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                                {showConfirmPassword && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    class="w-4 h-4 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowConfirmPassword(!showConfirmPassword);
                                    }}
                                  >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                )}
                                {!showConfirmPassword && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    class="w-4 h-4 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowConfirmPassword(!showConfirmPassword);
                                    }}
                                  >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            {formState.errors?.confirmPassword && <div className="text-xs text-rose-600 pt-1 pl-1">{formState.errors?.confirmPassword?.message}</div>}
                          </div>
                        )}
                      />
                    </div>

                    <div className="flex justify-end mt-6">
                      <button type="submit" disabled={loading} className={"flex items-center btn !bg-indigo-500 hover:!bg-indigo-600 text-white whitespace-nowrap " + (!loading ? "" : disabledCss)}>
                        Change Password
                        {loading && (
                          <span className="ml-2">
                            <svg className="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                            </svg>
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center flex-col">
                    <div className="w-12 h-12 rounded-full bg-rose-600 text-white flex justify-center items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="font-md font-inter font-semibold py-3">
                      <span>Your reset password verification code is invalid or expired.</span>
                      {/* <span
                        className="text-blue-500 cursor-pointer underline pl-1"
                        // onClick={() => {
                        //   handleClick();
                        // }}
                      >
                        here
                      </span> */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="hidden md:block absolute top-0 bottom-0 right-0 md:w-1/2" aria-hidden="true">
            <img className="object-cover object-center w-full h-full" src={require("../assets/img/login-image.jpeg")} width="760" height="1024" alt="Authentication" />
          </div>
        </div>
      )}
    </main>
  );
};

export default ResetPasswordForm;
